// Custom label renderer for Sigma.js that draws text inside aggregate nodes
import { getTheme, THEMES } from '../../../lib/themeManager';

/**
 * Custom label renderer that draws count text inside cluster nodes (centered)
 * and normal labels next to regular nodes
 *
 * Label properties:
 * - clusterCount: Numeric count string (e.g., "5") - drawn inside cluster nodes
 * - clusterDescription: Text with suffix (e.g., "compound + 4") - drawn outside cluster nodes
 * - nodeTitle: Regular node title (e.g., "compound name") - drawn outside regular nodes
 */
export function drawNodeLabel(context, data, settings) {
  const { key, clusterCount, clusterDescription, nodeTitle, x, y, size, color, labelOpacity, highlighted } = data;

  if (!size) return;

  // Get label opacity from node data (per-node), fallback to settings ref (global), default to 1.0
  const opacity = labelOpacity ?? settings.labelOpacityRef?.current ?? 1.0;

  // Save current globalAlpha and set new opacity
  const savedAlpha = context.globalAlpha;
  context.globalAlpha = opacity;

  // Determine node type from which properties are set
  const isClusterNode = clusterCount !== undefined && clusterCount !== "";

  // Determine which label to display
  const textLabel = isClusterNode ? clusterDescription : nodeTitle;

  // Node text labels and backgrounds should adapt to theme
  const isDarkMode = getTheme() === THEMES.DARK;

  // Labels use consistent colors in all states (highlighted or not)
  const labelColor = isDarkMode ? "#FFFFFF" : "#000000";
  const labelBgColor = isDarkMode ? "#1a1a1a" : "#FFFFFF"; // Match --bg-color
  const labelBorderColor = isDarkMode ? "#444444" : "#cccccc";

  // Cluster count text: always white for contrast on colored nodes
  const clusterTextColor = "#FFFFFF";

  // For highlighted nodes: draw pill-shaped white background with drop shadow
  if (highlighted && textLabel) {
    const fontSize = settings.labelSize || 12;
    context.font = `${fontSize}px sans-serif`;

    const labelPadding = 4;
    const labelX = x + size + 3;
    const labelY = y;

    // Calculate label dimensions
    const textWidth = context.measureText(textLabel).width;
    const labelBoxWidth = textWidth + labelPadding * 2;
    const labelBoxHeight = fontSize + labelPadding * 2;

    // White border width
    const borderWidth = 2;

    // Calculate white border circle radius:
    // - Should match label box height (labelBoxHeight / 2) to create smooth rounded edge
    // - BUT only if node diameter (size * 2) doesn't exceed labelHeight
    // - If node is large (size * 2 > labelHeight), use actual node size to accommodate it
    let whiteBorderRadius;
    if (size * 2 > labelBoxHeight) {
      // Large node: use actual size + border
      whiteBorderRadius = size + borderWidth;
    } else {
      // Small node: match label box height, but don't be smaller than node + border
      whiteBorderRadius = Math.max(size + borderWidth, labelBoxHeight / 2);
    }

    // Draw drop shadow all around the unified shape
    context.save();
    context.shadowColor = isDarkMode ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.5)";
    context.shadowBlur = 4;
    context.shadowOffsetX = 2;
    context.shadowOffsetY = 2;

    // Create unified path: rounded left edge + rectangular right side
    // Use consistent label background color, not node color
    context.fillStyle = labelBgColor;
    context.beginPath();

    // Calculate arc angles to intersect with rectangle at labelBoxHeight/2
    // When whiteBorderRadius > labelBoxHeight/2, arc needs to go beyond semicircle
    const halfLabelHeight = labelBoxHeight / 2;
    let startAngle, endAngle;

    if (whiteBorderRadius <= halfLabelHeight) {
      // Small node: arc endpoints align with or are inside rectangle
      // Use semicircle from -90° to +90°
      startAngle = -Math.PI / 2;
      endAngle = Math.PI / 2;
    } else {
      // Large node: calculate angle where arc intersects rectangle at ±labelBoxHeight/2
      const angle = Math.asin(halfLabelHeight / whiteBorderRadius);
      startAngle = -angle; // Top intersection (negative angle = above center)
      endAngle = angle;    // Bottom intersection (positive angle = below center)
    }

    // Start at top intersection point
    const startX = x + whiteBorderRadius * Math.cos(startAngle);
    const startY = y + whiteBorderRadius * Math.sin(startAngle);
    context.moveTo(startX, startY);

    // Draw arc from top intersection through left side to bottom intersection
    // counterclockwise=true makes arc bulge LEFT
    context.arc(x, y, whiteBorderRadius, startAngle, endAngle, true);

    // Now at bottom intersection point
    // Line across rectangle bottom to right edge
    context.lineTo(labelX + labelBoxWidth, y + halfLabelHeight);
    // Line up rectangle right edge
    context.lineTo(labelX + labelBoxWidth, y - halfLabelHeight);
    // Line across rectangle top back to arc start
    context.lineTo(startX, startY);

    context.closePath();
    context.fill();

    // Restore context (remove shadow for remaining drawing)
    context.restore();

    // Draw the node itself (with its color) on top of white background
    context.fillStyle = color;
    context.beginPath();
    context.arc(x, y, size, 0, Math.PI * 2);
    context.fill();

    // For cluster nodes when highlighted, immediately draw the count on top of the circle
    // This ensures the count is visible even when Sigma redraws the node
    if (isClusterNode && clusterCount) {
      const fontSize = Math.max(12, size * 0.6);
      context.fillStyle = clusterTextColor; // Inverted text color for contrast against colored node
      context.font = `${fontSize}px sans-serif`; // No bold - only for non-highlighted rendering
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillText(clusterCount, x, y);
    }

    // Draw label text with consistent color
    context.fillStyle = labelColor;
    context.textAlign = "left";
    context.textBaseline = "middle";
    context.fillText(textLabel, labelX + labelPadding, labelY);
  } else {
    // Non-highlighted labels: use original rendering

    if (isClusterNode) {
      // Draw count text centered inside the cluster node
      // Note: Sigma draws the node circle separately, this only draws the label
      const fontSize = Math.max(12, size * 0.6);  // Scale with node size
      context.fillStyle = clusterTextColor; // Inverted text color for contrast against colored node
      context.font = `${fontSize}px sans-serif`;
      context.textAlign = "center";
      context.textBaseline = "middle";

      // Draw count at node center (x, y are already in viewport coordinates)
      context.fillText(clusterCount, x, y);

      // If clusterDescription is present, also draw it to the right
      if (clusterDescription) {
        const textFontSize = settings.labelSize || 12;
        context.font = `${textFontSize}px sans-serif`;
        context.textAlign = "left";
        context.textBaseline = "middle";

        // Position label to the right of the node
        const labelX = x + size + 3;

        // Draw unified shape (arc around node + label rectangle) matching highlighted style
        const labelPadding = 2;
        const textWidth = context.measureText(clusterDescription).width;
        const labelBoxWidth = textWidth + labelPadding * 2;
        const labelBoxHeight = textFontSize + labelPadding * 2;

        const borderWidth = 1;
        let unifiedRadius;
        if (size * 2 > labelBoxHeight) {
          unifiedRadius = size + borderWidth;
        } else {
          unifiedRadius = Math.max(size + borderWidth, labelBoxHeight / 2);
        }

        const halfLabelHeight = labelBoxHeight / 2;
        let startAngle, endAngle;
        if (unifiedRadius <= halfLabelHeight) {
          startAngle = -Math.PI / 2;
          endAngle = Math.PI / 2;
        } else {
          const angle = Math.asin(halfLabelHeight / unifiedRadius);
          startAngle = -angle;
          endAngle = angle;
        }

        const startX = x + unifiedRadius * Math.cos(startAngle);
        const startY_arc = y + unifiedRadius * Math.sin(startAngle);

        context.fillStyle = labelBgColor;
        context.strokeStyle = labelBorderColor;
        context.lineWidth = borderWidth;
        context.beginPath();
        context.moveTo(startX, startY_arc);
        context.arc(x, y, unifiedRadius, startAngle, endAngle, true);
        context.lineTo(labelX + labelBoxWidth, y + halfLabelHeight);
        context.lineTo(labelX + labelBoxWidth, y - halfLabelHeight);
        context.lineTo(startX, startY_arc);
        context.closePath();
        context.fill();
        context.stroke();

        // Draw text fill
        context.fillStyle = labelColor;
        context.fillText(clusterDescription, labelX + labelPadding, y);
      }
    } else if (nodeTitle) {
      // Draw normal label next to the node
      const fontSize = settings.labelSize || 12;
      context.font = `${fontSize}px sans-serif`;
      context.textAlign = "left";
      context.textBaseline = "middle";

      // Position label to the right of the node
      const labelX = x + size + 3;

      // Draw unified shape (arc around node + label rectangle) matching highlighted style
      const labelPadding = 2;
      const textWidth = context.measureText(nodeTitle).width;
      const labelBoxWidth = textWidth + labelPadding * 2;
      const labelBoxHeight = fontSize + labelPadding * 2;

      const borderWidth = 1;
      let unifiedRadius;
      if (size * 2 > labelBoxHeight) {
        unifiedRadius = size + borderWidth;
      } else {
        unifiedRadius = Math.max(size + borderWidth, labelBoxHeight / 2);
      }

      const halfLabelHeight = labelBoxHeight / 2;
      let startAngle, endAngle;
      if (unifiedRadius <= halfLabelHeight) {
        startAngle = -Math.PI / 2;
        endAngle = Math.PI / 2;
      } else {
        const angle = Math.asin(halfLabelHeight / unifiedRadius);
        startAngle = -angle;
        endAngle = angle;
      }

      const startX = x + unifiedRadius * Math.cos(startAngle);
      const startY_arc = y + unifiedRadius * Math.sin(startAngle);

      context.fillStyle = labelBgColor;
      context.strokeStyle = labelBorderColor;
      context.lineWidth = borderWidth;
      context.beginPath();
      context.moveTo(startX, startY_arc);
      context.arc(x, y, unifiedRadius, startAngle, endAngle, true);
      context.lineTo(labelX + labelBoxWidth, y + halfLabelHeight);
      context.lineTo(labelX + labelBoxWidth, y - halfLabelHeight);
      context.lineTo(startX, startY_arc);
      context.closePath();
      context.fill();
      context.stroke();

      // Draw text fill
      context.fillStyle = labelColor;
      context.fillText(nodeTitle, labelX + labelPadding, y);
    }
  }

  // Restore original globalAlpha
  context.globalAlpha = savedAlpha;
}

/**
 * Hover renderer - no-op since we handle hover rendering in afterRender event
 *
 * We use afterRender instead of this custom hover renderer because:
 * 1. Hover renderers can't draw node circles (only labels)
 * 2. afterRender executes after ALL Sigma rendering, giving us full control
 * 3. afterRender has access to hover canvas for drawing on top of everything
 */
export function drawHoveredNodeLabel(context, data, settings) {
  // No-op - hover rendering handled via highlighted flag in drawNodeLabel
  // Sigma calls this for hovered nodes, but we handle hover rendering by setting
  // the 'highlighted' property in nodeReducer, which drawNodeLabel uses to render
  // the pill-shaped background and label
}